import type { CSSProperties, ReactNode } from "react"

import { ManualSection, Plate } from "./primitives"

// Scoped stacks, applied inline only inside this specimen so the global
// --font-body / --font-display tokens are never touched. The prototype
// faces are self-hosted (see fonts.css); nothing outside /design uses them.
const CURRENT_SERIF = '"Aleo", Georgia, serif'
const CURRENT_SANS = '"Raleway", system-ui, sans-serif'
const PROPOSED_SERIF = '"Newsreader", Georgia, serif'
const PROPOSED_SANS = '"Hanken Grotesk", system-ui, sans-serif'

function Lane({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-quaternary text-xs font-semibold tracking-wide uppercase">
        {label}
      </span>
      {children}
    </div>
  )
}

function Specimen({ serif, sans }: { serif: string; sans: string }) {
  const serifStyle: CSSProperties = { fontFamily: serif }
  const sansStyle: CSSProperties = { fontFamily: sans }
  return (
    <div className="flex flex-col gap-6">
      <Lane label="Editorial heading">
        <p
          style={{ ...serifStyle, fontWeight: 500 }}
          className="text-primary text-3xl leading-tight tracking-tight"
        >
          Songs for a long Tuesday
        </p>
      </Lane>
      <Lane label="Reading prose + italic aside">
        <p
          style={serifStyle}
          className="text-secondary max-w-prose text-lg leading-relaxed"
        >
          Someone made this for the version of you that needed it.{" "}
          <em style={{ ...serifStyle }} className="text-primary italic">
            I kept coming back to track three.
          </em>
        </p>
      </Lane>
      <Lane label="Body / UI">
        <p style={sansStyle} className="text-secondary max-w-prose">
          Product UI, labels, and body on dense surfaces. Friendly enough for
          the audience, serious enough to be trusted, and not a default.
        </p>
      </Lane>
      <Lane label="Label / button">
        <span
          style={{ ...sansStyle, fontWeight: 600 }}
          className="text-primary text-sm tracking-wide uppercase"
        >
          Suggest a track · 3 suggestions
        </span>
      </Lane>
    </div>
  )
}

// A proposal under evaluation, NOT adopted. The faces are prototyped here
// only so the team can judge Newsreader (serif) + Hanken Grotesk (sans)
// in-situ before deciding on an app-wide type re-platform.
export function TypeCandidates() {
  return (
    <ManualSection
      id="type-candidates"
      title="Type candidates under evaluation"
      lede="An external direction proposes swapping the editorial serif (Aleo → Newsreader) and the working sans (Raleway → Hanken Grotesk). That's an app-wide change against the committed type stack, so it's prototyped here only: self-hosted, scoped to this specimen, with the global tokens untouched. Judge the two stacks side by side; nothing else in the app has changed."
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Plate
          title="Current stack: Aleo + Raleway"
          note="The shipping stack: Aleo (slab serif) for editorial headings, Raleway (humanist sans) for body and UI."
        >
          <Specimen serif={CURRENT_SERIF} sans={CURRENT_SANS} />
        </Plate>
        <Plate
          title="Proposed stack: Newsreader + Hanken Grotesk"
          note="Newsreader (optical-size serif) for editorial headings and reading prose; Hanken Grotesk (warm grotesque) for body and UI. Static cuts here; the variable faces (incl. Newsreader's optical axis) would ship if adopted."
        >
          <Specimen serif={PROPOSED_SERIF} sans={PROPOSED_SANS} />
        </Plate>
      </div>
    </ManualSection>
  )
}
